// CLDR plural category selectors for ru + en. Only integers are categorised;
// non-integer counts always resolve to `other` (matches CLDR v44 defaults for
// our 2 locales).
import type { Locale, PluralEntry } from './types.js';

export type PluralCategory = 'zero' | 'one' | 'few' | 'many' | 'other';

export function selectRuCategory(n: number): PluralCategory {
  if (!Number.isInteger(n)) return 'other';
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'one';
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return 'few';
  if (mod10 === 0 || (mod10 >= 5 && mod10 <= 9) || (mod100 >= 11 && mod100 <= 14)) return 'many';
  return 'other';
}

export function selectEnCategory(n: number): PluralCategory {
  if (!Number.isInteger(n)) return 'other';
  return n === 1 ? 'one' : 'other';
}

export function selectCategory(locale: Locale, n: number): PluralCategory {
  return locale === 'ru' ? selectRuCategory(n) : selectEnCategory(n);
}

/**
 * Resolve a plural entry against a count using CLDR rules. `zero` slot wins
 * over `other` only when `count === 0` AND the catalog explicitly provides it.
 */
export function resolvePlural(entry: PluralEntry, locale: Locale, count: number): string {
  if (count === 0 && entry.zero !== undefined) return entry.zero;
  const cat = selectCategory(locale, count);
  switch (cat) {
    case 'zero':
      return entry.zero ?? entry.other;
    case 'one':
      return entry.one;
    case 'few':
      return entry.few ?? entry.other;
    case 'many':
      return entry.many ?? entry.other;
    case 'other':
      return entry.other;
  }
}
